import { useLocalization } from "@fluent/react";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useApi } from "@lending/lib/api";
import { getOlbConfirmInfoPath } from "../components/applicationHelpers";
import type { ProductType } from "@lending/navigation";

/**
 * Launches a prefilled application from a Loan Portal sidebar link.
 *
 * One endpoint per product serves both portal principals (a digital-banking user and a
 * verified-identity session); the API client already sends the right credentials for the
 * host, so there is no authMode branching here. The endpoint always creates a new
 * application server-side and prefills it from the principal's most recent prior
 * application (resuming an in-progress one is done from the Loan Portal list, not here),
 * then we navigate to the OLB confirm-info screen.
 */
function useLaunchPortalApplication() {
  const $api = useApi();
  const navigate = useNavigate();
  const { l10n } = useLocalization();
  const { showErrorToast } = useToastsContext();
  const [launchingProduct, setLaunchingProduct] = useState<ProductType | null>(null);

  const personalLoanMutation = $api.useMutation(
    "post",
    "/v2/lending/personal_loan_applications/prefill_from_portal/"
  );
  const creditCardMutation = $api.useMutation(
    "post",
    "/v2/lending/credit_card_applications/prefill_from_portal/"
  );
  const autoLoanMutation = $api.useMutation(
    "post",
    "/v2/lending/vehicle_loan_applications/prefill_from_portal/"
  );

  const mutationByProduct = {
    "personal-loan": personalLoanMutation,
    "credit-card": creditCardMutation,
    "auto-loan": autoLoanMutation,
  } as const;

  const launchError = l10n.getString(
    "loan-portal-launch-error",
    null,
    "We couldn't start your application. Please try again."
  );

  const launchProduct = async (productType: ProductType) => {
    // Guard against a second launch while one is already in flight.
    if (launchingProduct) {
      return;
    }
    setLaunchingProduct(productType);

    try {
      const application = await mutationByProduct[productType].mutateAsync({});
      if (!application.id) {
        throw new Error("Prefilled application response is missing an id");
      }
      // On success we leave the portal, so launchingProduct stays set until unmount.
      await navigate(getOlbConfirmInfoPath(productType, application.id));
    } catch (error) {
      Sentry.captureException(error, { extra: { productType } });
      showErrorToast(launchError);
      setLaunchingProduct(null);
    }
  };

  return { launchProduct, launchingProduct };
}

export { useLaunchPortalApplication };
